/**
 * Pricing staleness detection utility.
 *
 * Detects whether local pricing data is outdated so `fathom go`
 * can warn users and `fathom research --auto-update` can refresh.
 */
import { existsSync, readFileSync, writeFileSync, mkdirSync } from "node:fs";
import { resolve } from "node:path";

export interface StalenessResult {
  stale: boolean;
  daysSinceUpdate?: number;
  source?: string;
}

export interface StalenessOptions {
  /** Project root directory (default: process.cwd()) */
  projectDir?: string;
  /** Data directory containing models.yaml (default: resolved from package) */
  dataDir?: string;
  /** Number of days before data is considered stale (default: 30) */
  thresholdDays?: number;
}

/**
 * Check whether pricing data is stale.
 *
 * 1. Reads `.fathom/pricing-updated.json` for a recorded timestamp.
 * 2. Falls back to parsing `# Last verified: YYYY-MM-DD` from models.yaml header.
 * 3. If neither found, assumes stale (source: "unknown").
 */
export function checkPricingStaleness(opts?: StalenessOptions): StalenessResult {
  const projectDir = opts?.projectDir ?? process.cwd();
  const thresholdDays = opts?.thresholdDays ?? 30;

  // Strategy 1: Read .fathom/pricing-updated.json
  const timestampPath = resolve(projectDir, ".fathom", "pricing-updated.json");
  if (existsSync(timestampPath)) {
    try {
      const raw = readFileSync(timestampPath, "utf-8");
      const data = JSON.parse(raw) as { lastUpdated?: string };
      if (data.lastUpdated) {
        const lastDate = new Date(data.lastUpdated);
        if (!isNaN(lastDate.getTime())) {
          return evaluateStaleness(lastDate, thresholdDays, "pricing-updated.json");
        }
      }
    } catch {
      // Corrupt file — fall through to next strategy
    }
  }

  // Strategy 2: Parse models.yaml header
  const dataDir = opts?.dataDir ?? resolveDefaultDataDir();
  const modelsPath = resolve(dataDir, "models.yaml");
  if (existsSync(modelsPath)) {
    try {
      const raw = readFileSync(modelsPath, "utf-8");
      // Read only first 5 lines
      const headerLines = raw.split("\n").slice(0, 5).join("\n");
      const match = headerLines.match(/# Last verified: (\d{4}-\d{2}-\d{2})/);
      if (match) {
        const lastDate = new Date(match[1] + "T00:00:00Z");
        if (!isNaN(lastDate.getTime())) {
          return evaluateStaleness(lastDate, thresholdDays, "models.yaml");
        }
      }
    } catch {
      // Can't read — fall through
    }
  }

  // Strategy 3: Unknown — assume stale
  return { stale: true, source: "unknown" };
}

/**
 * Record that pricing data was just updated.
 * Writes current ISO timestamp to `.fathom/pricing-updated.json`.
 */
export function recordPricingUpdate(projectDir?: string): void {
  const dir = projectDir ?? process.cwd();
  const fathomDir = resolve(dir, ".fathom");
  mkdirSync(fathomDir, { recursive: true });
  writeFileSync(
    resolve(fathomDir, "pricing-updated.json"),
    JSON.stringify({ lastUpdated: new Date().toISOString() }, null, 2) + "\n",
  );
}

// ── Internal helpers ──

function evaluateStaleness(
  lastDate: Date,
  thresholdDays: number,
  source: string,
): StalenessResult {
  const now = new Date();
  const diffMs = now.getTime() - lastDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays >= thresholdDays) {
    return { stale: true, daysSinceUpdate: diffDays, source };
  }
  return { stale: false, daysSinceUpdate: diffDays, source };
}

function resolveDefaultDataDir(): string {
  // Same resolution logic as research.ts
  const bundled = resolve(import.meta.dirname ?? __dirname, "data");
  if (existsSync(resolve(bundled, "models.yaml"))) return bundled;
  return resolve(import.meta.dirname ?? __dirname, "../../../../data");
}
